import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateCurrencyRequestSchema,
  type CreateCurrencyRequest,
  type CurrencyDetail,
  type CurrencyListItem,
  UpdateCurrencyRequestSchema,
  type UpdateCurrencyRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateCurrency, useUpdateCurrency } from '../use-currencies';

interface CurrencyFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  currency?: CurrencyDetail | CurrencyListItem | null;
}

const CREATE_DEFAULTS: CreateCurrencyRequest = {
  code: '',
  name: '',
  symbol: '',
  decimals: 2,
  sortOrder: 0,
  isActive: true,
};

const CurrencyFields = ({ pending }: { pending: boolean }): React.JSX.Element => (
  <>
    <UzFormField name="code">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value.toUpperCase())}
          label="Code"
          placeholder="INR"
          hint="ISO 4217 (3 letters)."
          maxLength={3}
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="Indian Rupee"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="symbol">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Symbol"
          placeholder="₹"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="decimals">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Decimals"
          hint="Fraction digits (e.g. 2 for INR, 0 for JPY)."
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const CurrencyFormDrawer = ({
  open,
  onOpenChange,
  mode,
  currency,
}: CurrencyFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createCurrency = useCreateCurrency();
  const updateCurrency = useUpdateCurrency();
  const pending = createCurrency.isPending || updateCurrency.isPending;

  const createForm = useForm<CreateCurrencyRequest>({
    resolver: zodResolver(CreateCurrencyRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateCurrencyRequest>({
    resolver: zodResolver(UpdateCurrencyRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (currency) {
      updateForm.reset({
        code: currency.code,
        name: currency.name,
        symbol: currency.symbol ?? '',
        decimals: currency.decimals,
        sortOrder: currency.sortOrder,
        isActive: currency.isActive,
      });
    }
  }, [open, isCreate, currency, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createCurrency.mutateAsync({
      ...values,
      symbol: values.symbol?.trim() ? values.symbol : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!currency) return;
    await updateCurrency.mutateAsync({
      id: currency.id,
      payload: { ...values, symbol: values.symbol?.trim() ? values.symbol : null },
    });
    onOpenChange(false);
  });

  const fields = <CurrencyFields pending={pending} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add currency' : `Edit ${currency?.code ?? 'currency'}`}
      description="ISO 4217 code, display symbol, and the number of fraction digits."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
